import React from "react";
import { Box, Container, Typography, Button, Grid, Card, CardMedia } from "@mui/material";
import { motion } from "framer-motion";

const users = [
  {
    type: "Indoor Wireless AP",
    description: "High-performance indoor access points designed for seamless coverage in offices, retail spaces, and warehouses.",
    image: "https://storage.googleapis.com/app_darkendimg/data-speedway/in-door-wap.mp4",
    bg: "#121212",
  },
  {
    type: "Outdoor Wireless AP",
    description: "Ruggedized outdoor access points for campus-wide coverage, mesh networking, and harsh environment connectivity.",
    image: "https://storage.googleapis.com/app_darkendimg/data-speedway/in-out.mp4",
    bg: "#ebecf0",
  },
  {
    type: "Predictive Heatmaps",
    description: "Advanced RF planning and predictive heatmaps to simulate coverage and identify optimal AP placement before installation.",
    image: "https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80",
    bg: "#121212",
  },
  {
    type: "Post-Installation Surveys",
    description: "Comprehensive validation surveys to verify signal strength, SNR, and roaming performance after deployment.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80",
    bg: "#ebecf0",
  },
  {
    type: "Wireless Support",
    description: "Ongoing network management, firmware updates, and troubleshooting to ensure your wireless estate remains secure and fast.",
    image: "https://images.unsplash.com/photo-1558494949-ef2bb6db8744?auto=format&fit=crop&w=600&q=80",
    bg: "#121212",
  },
];

const HeroSection = () => (
  <Box
    sx={{
      position: "relative",

      height: { xs: "400px", sm: "500px", md: "600px" },
      overflow: "hidden",
    }}
  >
    {/* Video Background */}
    <video
      autoPlay
      loop
      muted
      playsInline
      style={{
        width: "100%",
        height: "100%",
        objectFit: "cover",
      }}
      poster="https://storage.googleapis.com/app_darkendimg/assets/koffiehands.png" // fallback image
    >
      <source
        src="https://storage.googleapis.com/app_darkendimg/data-speedway/wifi.mp4"
        type="video/mp4"
      />
      Your browser does not support the video tag.
    </video>

    {/* Content Overlay */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,

        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        textAlign: "center",
        p: { xs: 2, sm: 5 },
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <Typography variant="h2" fontWeight="bold" sx={{ textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}>
          Professional Wireless Services
        </Typography>
        <Typography variant="h4" mt={2} sx={{ color: '#FACD00', textShadow: "2px 2px 4px rgba(0, 0, 0, 0.5)" }}>
          Nationwide site surveys, heat mapping, and AP installation.
        </Typography>
        <Box mt={4}>
          <Button variant="contained" sx={{ mr: 2, backgroundColor: '#facd00', color: '#000' }}>
            Get Started
          </Button>
          <Button variant="outlined" sx={{ borderColor: '#000', color: '#FACD00' }}>
            Learn More
          </Button>
        </Box>
      </motion.div>
    </Box>
  </Box>
);

const UserSection = ({ type, description, image, bg, index }) => {
  const isEven = index % 2 === 0;

  // Check if the URL points to a video
  const isVideo = image?.toLowerCase().endsWith('.mp4');

  // Create a renderMedia function to simplify the JSX below
  const renderMedia = () => {
    if (isVideo) {
      return (
        <CardMedia
          component="video"
          height="300"
          src={image} // This pulls from your JSON 'image' field
          sx={{
            borderRadius: '10px', // Use a string like '20px' or a number (MUI multiplier)
            backgroundColor: '#fff',
            overflow: 'hidden',   // Essential to clip the video to the border radius
            objectFit: 'cover'    // Ensures the video fills the area nicely
          }}
          autoPlay
          loop
          muted
          controls={false}
        />
      );
    }
    // Remember to return null or an <img> tag if isVideo is false
    return null;
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: isEven ? -100 : 100 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1 }}
    >
      <Box sx={{ backgroundColor: bg, py: 6 }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            {isEven ? (
              <>
                <Grid item xs={12} md={6}>
                  {renderMedia()}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: 3, borderRadius: 3, p: 3 }}>
                    <Typography variant="h4" fontWeight="bold">
                      {type}
                    </Typography>
                    <Typography variant="body1" mt={2}>
                      {description}
                    </Typography>
                  </Card>
                </Grid>
              </>
            ) : (
              <>
                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: 3, borderRadius: 3, p: 3 }}>
                    <Typography variant="h4" fontWeight="bold">
                      {type}
                    </Typography>
                    <Typography variant="body1" mt={2}>
                      {description}
                    </Typography>
                  </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  {renderMedia()}
                </Grid>
              </>
            )}
          </Grid>
        </Container>
      </Box>
    </motion.div>
  );
};

const RoasterPage = () => {
  return (
    <>
      <HeroSection />
      {users.map((user, index) => (
        <UserSection key={index} index={index} {...user} />
      ))}
    </>
  );
};

export default RoasterPage;
